'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  FiFileText, 
  FiClock, 
  FiTerminal,
  FiCode,
  FiCopy,
  FiUpload,
  FiFile,
  FiTrash2,
  FiEye,
  FiCheck,
  FiZap,
  FiGlobe,
  FiBookOpen
} from 'react-icons/fi'
import Swal from 'sweetalert2'

export default function XmlPdfPage() {
  const [token, setToken] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState<FileList | null>(null)
  const [format, setFormat] = useState('a4')
  const [tipoDocumento, setTipoDocumento] = useState('01')
  const [masivo, setMasivo] = useState('0')
  const [responseTime, setResponseTime] = useState<string | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('curl')
  const [copyCodeSuccess, setCopyCodeSuccess] = useState(false)

  useEffect(() => {
    fetchToken()
  }, [])

  const fetchToken = async () => {
    try {
      const res = await fetch('/api/user/token')
      const data = await res.json()
      if (data.success) setToken(data.token)
    } catch (e) {
      console.error(e)
    }
  }

  const handleProcess = async () => {
    if (!token) {
        return Swal.fire('Error', 'Ingresa un token de acceso', 'error')
    }
    if (!files || files.length === 0) {
        return Swal.fire('Error', 'Selecciona al menos un archivo XML', 'error')
    }

    setLoading(true)
    setResponseTime(null)
    setPdfUrl(null)

    const startTime = performance.now()
    const endpoint = 'https://convertir.miapi.cloud/generate-pdf/'

    try {
      const formData = new FormData()
      Array.from(files).forEach((file) => {
        formData.append('files', file)
      })
      formData.append('format', format)
      formData.append('tipo_documento', tipoDocumento)
      formData.append('masivo', masivo)

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.trim()}`
        },
        body: formData
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Error desconocido' }))
        throw new Error(errorData.error || 'Error al procesar el archivo')
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      
      const endTime = performance.now()
      const timeSecs = (endTime - startTime) / 1000
      setResponseTime(timeSecs < 1 ? `${(timeSecs * 1000).toFixed(0)}ms` : `${timeSecs.toFixed(2)}s`)
      
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Archivo procesado con éxito',
        showConfirmButton: false,
        timer: 3000
      })
    } catch (e: any) {
      Swal.fire('Error', e.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopyCodeSuccess(true)
    setTimeout(() => setCopyCodeSuccess(false), 2000)
  }

  const snippets: any = {
    curl: `curl -X POST "https://convertir.miapi.cloud/generate-pdf/" \\
  -H "Authorization: Bearer ${token || 'TU_TOKEN'}" \\
  -F "files=@documento.xml" \\
  -F "format=a4" \\
  -F "tipo_documento=01" \\
  -F "masivo=0"`,
    php: `<?php
$token = "${token || 'TU_TOKEN'}";
$ch = curl_init("https://convertir.miapi.cloud/generate-pdf/");
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer $token"]);
curl_setopt($ch, CURLOPT_POSTFIELDS, [
    "files" => new CURLFile("documento.xml"),
    "format" => "a4",
    "tipo_documento" => "01",
    "masivo" => "0"
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
file_put_contents("comprobante.pdf", $response);`,
    js: `const formData = new FormData();
formData.append("files", xmlFile);
formData.append("format", "a4");
formData.append("tipo_documento", "01");
formData.append("masivo", "0");

const res = await fetch("https://convertir.miapi.cloud/generate-pdf/", {
  method: "POST",
  headers: { "Authorization": "Bearer ${token || 'TU_TOKEN'}" },
  body: formData
});
const blob = await res.blob();
const url = URL.createObjectURL(blob);
window.open(url);`
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-gray-900">Convertir XML a PDF</h1>
        <p className="text-gray-500">Transforma tus archivos XML de SUNAT en documentos PDF legibles (A4 o Ticket).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-none shadow-sm h-full">
          <CardHeader className="bg-gray-50/50 border-b py-4">
            <CardTitle className="text-sm font-bold flex flex-col gap-1">
                <span>Configuración de Conversión</span>
                <span className="text-[10px] font-normal text-gray-500 uppercase tracking-widest">
                  Tipo: <span className="text-blue-600 font-bold">POST (form-data)</span> | Respuesta: <span className="text-red-600 font-bold">PDF / ZIP</span>
                </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100/50 text-xs text-blue-800 leading-relaxed">
                Carga uno o más archivos XML. Si seleccionas "Masivo", recibirás un archivo ZIP con todos los PDFs.
            </div>

            <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Masivo</label>
                        <select 
                            value={masivo}
                            onChange={(e) => setMasivo(e.target.value)}
                            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        >
                            <option value="0">No</option>
                            <option value="1">Sí</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Tipo Doc.</label>
                        <select 
                            value={tipoDocumento}
                            onChange={(e) => setTipoDocumento(e.target.value)}
                            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        >
                            <option value="01">Factura</option>
                            <option value="03">Boleta</option>
                            <option value="07">Nota Crédito</option>
                            <option value="08">Nota Débito</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Formato</label>
                        <select 
                            value={format}
                            onChange={(e) => setFormat(e.target.value)}
                            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        >
                            <option value="a4">A4</option>
                            <option value="ticket">Ticket</option>
                        </select>
                    </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Bearer Token</label>
                  <input 
                      type="text" 
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Ingresa tu token"
                      className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div className="space-y-2">
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Archivos XML</label>
                   <div className="relative">
                      <input 
                        type="file" 
                        multiple 
                        accept=".xml" 
                        id="xml-upload" 
                        className="hidden" 
                        onChange={(e) => setFiles(e.target.files)}
                      />
                      <label 
                        htmlFor="xml-upload"
                        className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl p-8 hover:bg-gray-50 hover:border-blue-400 transition-all cursor-pointer group"
                      >
                         <FiUpload className="h-8 w-8 text-gray-300 group-hover:text-blue-500 mb-2" />
                         <span className="text-xs text-gray-500">{files ? `${files.length} archivos seleccionados` : 'Haz clic para cargar o arrastra tus archivos'}</span>
                      </label>
                   </div>
                </div>

                <Button 
                    onClick={handleProcess}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-6 rounded-xl font-bold shadow-lg shadow-blue-100 gap-2"
                >
                    {loading ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Procesar y Generar PDF'}
                </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm min-h-[450px] flex flex-col overflow-hidden bg-slate-100/50">
          <CardHeader className="flex flex-row items-center justify-between border-b py-4 bg-white/50 backdrop-blur-sm">
            <CardTitle className="text-sm font-bold flex items-center gap-2 text-gray-700">
              <FiEye className="text-blue-500" /> Vista Previa
            </CardTitle>
            <div className="flex items-center gap-2">
              {responseTime && (
                <span className="text-[10px] font-bold text-gray-400 bg-white px-2 py-1 rounded-md border flex items-center gap-1">
                  <FiClock className="h-3 w-3" /> {responseTime}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden relative">
             {pdfUrl ? (
                <iframe src={pdfUrl} className="w-full h-full border-none" title="Vista previa PDF" />
             ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-4">
                    <div className="w-20 h-20 rounded-full bg-gray-200/50 flex items-center justify-center">
                        <FiFile className="h-10 w-10 text-gray-300" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em]">Esperando generación de PDF...</p>
                </div>
             )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6 pt-6">
        <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200">
                <FiBookOpen className="text-white h-5 w-5" />
            </div>
            <div>
                <h2 className="text-xl font-bold text-gray-900">Documentación de la API</h2>
                <p className="text-xs text-gray-500 font-medium">Integra la conversión de XML a PDF en tus propias aplicaciones.</p>
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
                <Card className="border-none shadow-sm">
                    <CardHeader className="py-4 border-b bg-gray-50/30">
                        <CardTitle className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2">
                            <FiZap className="text-amber-500" /> Parámetros del Body
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y text-[11px]">
                            <div className="p-4 space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono font-bold text-blue-600">files</span>
                                    <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">Requerido</span>
                                </div>
                                <p className="text-gray-500">Uno o más archivos XML en formato binario (multipart/form-data).</p>
                            </div>
                            <div className="p-4 space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono font-bold text-blue-600">format</span>
                                    <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">Opcional</span>
                                </div>
                                <p className="text-gray-500">Formato de salida: <code className="text-blue-500">a4</code> (default) o <code className="text-blue-500">ticket</code>.</p>
                            </div>
                            <div className="p-4 space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono font-bold text-blue-600">tipo_documento</span>
                                    <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">Opcional</span>
                                </div>
                                <p className="text-gray-500">Códigos SUNAT: 01 (Factura), 03 (Boleta), 07 (NC), 08 (ND).</p>
                            </div>
                            <div className="p-4 space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono font-bold text-blue-600">masivo</span>
                                    <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">Opcional</span>
                                </div>
                                <p className="text-gray-500">Si se activa (<code className="text-blue-500">1</code>), retorna un ZIP con múltiples PDFs.</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <div className="bg-blue-600 rounded-2xl p-6 text-white shadow-xl shadow-blue-100 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
                        <FiGlobe className="h-24 w-24" />
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-1">Base Endpoint</p>
                    <p className="font-mono text-sm font-bold break-all">https://convertir.miapi.cloud/generate-pdf/</p>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="px-2 py-1 bg-white/20 rounded font-bold text-[10px] uppercase">POST</span>
                        <span className="px-2 py-1 bg-white/20 rounded font-bold text-[10px] uppercase">Form-Data</span>
                    </div>
                </div>
            </div>

            <Card className="lg:col-span-2 border-none shadow-sm overflow-hidden flex flex-col">
                <CardHeader className="py-0 px-0 bg-gray-900 border-none">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                        <div className="flex items-center gap-6">
                            <button 
                                onClick={() => setActiveTab('curl')}
                                className={`text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'curl' ? 'text-blue-400 border-b-2 border-blue-400 pb-1' : 'text-gray-500 hover:text-gray-300'}`}
                            >Curl</button>
                            <button 
                                onClick={() => setActiveTab('php')}
                                className={`text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'php' ? 'text-blue-400 border-b-2 border-blue-400 pb-1' : 'text-gray-500 hover:text-gray-300'}`}
                            >PHP</button>
                            <button 
                                onClick={() => setActiveTab('js')}
                                className={`text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'js' ? 'text-blue-400 border-b-2 border-blue-400 pb-1' : 'text-gray-500 hover:text-gray-300'}`}
                            >Node / JS</button>
                        </div>
                        <button 
                            onClick={() => copyToClipboard(snippets[activeTab])}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${copyCodeSuccess ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                        >
                            {copyCodeSuccess ? <><FiCheck /> Copiado</> : <><FiCopy /> Copiar Código</>}
                        </button>
                    </div>
                </CardHeader>
                <CardContent className="flex-1 bg-[#0d1117] p-6 font-mono text-xs text-blue-100 overflow-auto custom-scrollbar leading-relaxed">
                    <pre className="whitespace-pre-wrap">
                        {snippets[activeTab]}
                    </pre>
                </CardContent>
            </Card>
        </div>
      </div>
    </div>
  )
}
